import React, { Component } from "react";
import { hasBecomeTrue } from "~/hocs/crud";
import { messages } from "./../../i18n-messages";
import { InjectedIntlProps, injectIntl } from "@ai360/core/intl";
import { model, service } from "./../data";
import {
    getAgBytesErrorClassNames,
    handlePicklistChange,
    mapToPicklistValue,
    memoizeSelectableOptions,
    onNumberChange,
    onTextChange,
    prepareSelectableOptions,
} from "~/admin/utils";
// Components
import { Checkbox, NumericInput, Section, SelectInput, SubSection, TextInput } from "~/core";
// constants
import { adminData, ID, PICKLIST_GUID, VALUE } from "~/admin/data";
import * as picklistService from "~/core/picklist/picklist-names";
import { GrowthStage, IAddEditPanelState, IAddEditProps, PickListValue } from "../interfaces";

export class AddEditPanel extends Component<IAddEditProps & InjectedIntlProps, IAddEditPanelState> {
    growthStage: GrowthStage;

    getGrowthStageGroupTypeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );

    constructor(props) {
        super(props);
        this.growthStage = props.record || {};
        this.state = {
            nextId: null,
        };
    }

    componentDidMount(): void {
        this.setupGrowthStageRecord();
        this.props.setBreadcrumbs([""]);
        const { needs, recordGuid } = this.props;
        needs([this.props.getNextId(), this.props.fetchPicklistData()]);

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(recordGuid)]);
        }
    }

    componentDidUpdate(prevProps: IAddEditProps & InjectedIntlProps): void {
        if (hasBecomeTrue("fetchData", prevProps, this.props)) {
            this.props.liftRecordData(this.growthStage);
        }

        if (this.props.addEditPanel.mode === "ADD") {
            this.setupNextId(this.props);
        } else {
            if (this.props.record && this.props.record !== prevProps.record) {
                this.growthStage = this.props.record;
                this.setHeaderText();
            }
        }
    }

    setupNextId = (props: IAddEditProps & InjectedIntlProps): void => {
        if (props.nextId && props.nextId !== this.state.nextId) {
            this.growthStage[model.PROPS_GROWTH_STAGE_ID] = "" + props.nextId;
            this.setState({
                nextId: props.nextId,
            });
        }
    };

    setupGrowthStageRecord = (): void => {
        const { addEditPanel } = this.props;
        this.growthStage = {};
        if (addEditPanel.mode === "ADD") {
            this.growthStage = service.getDefaultRecord();
        }
    };

    onTextChange = (formKey: string, value: string | boolean, callback?: () => void): void => {
        this.growthStage = onTextChange(this.growthStage, { formKey: [formKey], value }, callback);
    };

    onNumberChange = (formKey: string, value: number, callback?: () => void): void => {
        this.growthStage = onNumberChange(
            this.growthStage,
            { formKey: [formKey], value },
            callback
        );
    };

    onPicklistChange = (
        { type, guid }: { type: string; guid: string },
        value: PickListValue,
        callback?: () => void
    ): void => {
        this.growthStage = handlePicklistChange(this.growthStage, { type, guid, value }, callback);
    };

    setHeaderText = (): void => {
        this.props.setBreadcrumbs([this.growthStage[model.PROPS_GROUP_NAME]]);
    };

    renderGrowStageInfo = (): JSX.Element => {
        const { apiErrors, userRole } = this.props;
        const { formatMessage } = this.props.intl;
        const { growthStage } = this;
        return (
            <div className="section-container">
                <Section testId="growth-stage-group-general-section">
                    <SubSection testId="growth-stage-group-id-subsection">
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={9}
                            suppressFormatting
                            placeholderText={formatMessage(messages.growthStageGroupID)}
                            containerClassNames={getAgBytesErrorClassNames([162, 163], apiErrors, [
                                "form-input-id",
                            ])}
                            value={"" + growthStage[model.PROPS_GROWTH_STAGE_ID]}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_GROWTH_STAGE_ID, value)
                            }
                            required
                            testId="growth-stage-group-id-input"
                        />
                        {!userRole[model.PROPS_ACTIVE_INACTIVE] ? null : (
                            <Checkbox
                                className="active-checkbox"
                                onChange={(e, value) =>
                                    this.onTextChange(model.PROPS_ACTIVE_YN, value)
                                }
                                value={growthStage[model.PROPS_ACTIVE_YN]}
                                label={formatMessage(messages.active)}
                                testId="growth-stage-group-is-active-checkbox"
                            />
                        )}
                    </SubSection>
                    <SubSection testId="growth-stage-group-name-and-type-subsection">
                        <TextInput
                            tabIndex={0}
                            maxLength={50}
                            containerClassNames={getAgBytesErrorClassNames(161, apiErrors)}
                            placeholderText={formatMessage(messages.growthStageGroupName)}
                            value={growthStage[model.PROPS_GROUP_NAME]}
                            autoFocus
                            onChange={(value) =>
                                this.onTextChange(model.PROPS_GROUP_NAME, value, this.setHeaderText)
                            }
                            required
                            testId="growth-stage-group-name-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            required
                            containerClassNames={getAgBytesErrorClassNames([160, 522], apiErrors)}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getGrowthStageGroupTypeOptions(
                                this.props[picklistService.PICKLIST_GROWTH_STAGE_GROUP_TYPE]
                            )}
                            placeholderText={formatMessage(messages.growthStageGroupType)}
                            value={mapToPicklistValue({
                                options: this.getGrowthStageGroupTypeOptions(
                                    this.props[picklistService.PICKLIST_GROWTH_STAGE_GROUP_TYPE]
                                ),
                                selectedGuid: this.growthStage[model.PROPS_GROWTH_STAGE_GROUP_TYPE],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_GROWTH_STAGE_GROUP_TYPE,
                                        guid: model.PROPS_GROWTH_STAGE_GROUP_TYPE,
                                    },
                                    value
                                );
                            }}
                            testId="growth-stage-group-type-select"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    render(): JSX.Element {
        return <div className="add-edit-panel">{this.renderGrowStageInfo()}</div>;
    }
}

export default injectIntl(AddEditPanel);
